import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, mediaQueryLarge, getActiveCollectionGrid, getActiveCollectionPaginationBar } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {string | null} */
  #imageRatioSetting = null;

  /** @type {IntersectionObserver | null} */
  #infiniteObserver = null;

  /** @type {boolean} */
  #infiniteLoading = false;

  /** @type {number} */
  #highestLoadedPage = 1;

  /** Consecutive infinite-scroll fetch failures before requiring Try again */
  static #INFINITE_MAX_AUTO_RETRIES = 3;

  /** @type {number} */
  #infiniteFailCount = 0;

  /** @type {boolean} */
  #infinitePausedForError = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #infiniteRetryTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();

    // Attach pagination click handler first (document-level so it always receives clicks)
    this.#attachPaginationClickHandler();

    // Defer bar setup so DOM/refs are ready; update state when bar is present
    requestAnimationFrame(() => {
      this.#setupPaginationBar();
    });

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.addEventListener('shopify:section:load', this.#handleSectionLoad);
    // Fired by facets.js after section morph completes (FilterUpdate is too early)
    document.addEventListener('collection:filters:rendered', this.#handleFiltersRendered);
  }

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onPaginationClick, true);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
    document.removeEventListener('collection:filters:rendered', this.#handleFiltersRendered);
    this.#clearInfiniteRetryTimer();
    this.#infiniteObserver?.disconnect();
    this.#infiniteObserver = null;
  }

  /** @type {number} */
  #currentPage = 1;

  /** @type {(event: MouseEvent) => void} */
  #onPaginationClick;

  /**
   * Get current page from URL (e.g. ?page=2)
   */
  #getCurrentPageFromURL() {
    const params = new URLSearchParams(window.location.search);
    const p = params.get('page');
    const num = p ? parseInt(p, 10) : 1;
    return Number.isNaN(num) || num < 1 ? 1 : num;
  }

  /**
   * Resolve a ref by name (component refs or querySelector fallback)
   * @param {string} name
   * @returns {HTMLElement | undefined}
   */
  #getRef(name) {
    if (name === 'grid') {
      const activeGrid = getActiveCollectionGrid(this);
      if (activeGrid) return activeGrid;
    }
    if (name === 'paginationBar') {
      const activeBar = getActiveCollectionPaginationBar(this);
      if (activeBar) return activeBar;
    }
    return this.refs[name] ?? this.querySelector(`[ref="${name}"]`) ?? undefined;
  }

  /**
   * Get total number of pages from grid or pagination bar
   */
  #getLastPage() {
    const paginationBar = this.#getRef('paginationBar');
    const grid = this.#getRef('grid');
    const fromBar = paginationBar?.getAttribute?.('data-last-page');
    if (fromBar) return parseInt(fromBar, 10) || 1;
    const fromGrid = grid?.getAttribute?.('last-page');
    if (fromGrid) return parseInt(fromGrid, 10) || 1;
    return 1;
  }

  /**
   * Attach document-level click handler for pagination so clicks are always received
   * (avoids refs/timing issues; only handles clicks inside this component).
   */
  #attachPaginationClickHandler() {
    this.#onPaginationClick = (event) => {
      if (!this.isConnected || !this.contains(event.target)) return;

      const clickedBar = event.target.closest('.pagination-bar');
      if (clickedBar) {
        const activeBar = getActiveCollectionPaginationBar(this);
        if (activeBar && clickedBar !== activeBar) return;
      }

      const lastPage = this.#getLastPage();
      if (event.target.closest('[ref="prevPage"]')) {
        event.preventDefault();
        if (this.#currentPage > 1) this.#goToPage(this.#currentPage - 1);
        return;
      }
      if (event.target.closest('[ref="nextPage"]')) {
        event.preventDefault();
        if (this.#currentPage < lastPage) this.#goToPage(this.#currentPage + 1);
        return;
      }
      const pageLink = event.target.closest('a[data-page]');
      if (pageLink) {
        event.preventDefault();
        const pageNum = parseInt(pageLink.getAttribute('data-page'), 10);
        if (!Number.isNaN(pageNum) && pageNum >= 1 && pageNum <= lastPage) {
          this.#goToPage(pageNum);
        }
      }
    };
    document.addEventListener('click', this.#onPaginationClick, true);
  }

  /**
   * Cache the currently visible section HTML so Previous / back-to-page-1 is instant (no fetch).
   * Called on load so we never have to re-fetch the initial page.
   */
  #cacheCurrentPageHTML() {
    const section = this.closest('.shopify-section');
    if (!section?.id) return;
    const html = section.outerHTML;
    if (html) this.pages.set(this.#currentPage, html);
  }

  /**
   * Prefetch adjacent pages in the background so Next/Previous feel instant.
   * Prefetch previous page first when not on page 1 so "Previous" is more likely cached.
   */
  #prefetchAdjacentPages() {
    const lastPage = this.#getLastPage();
    const next = this.#currentPage + 1;
    const prev = this.#currentPage - 1;
    if (prev >= 1 && !this.pages.has(prev)) this.#fetchSpecificPage(prev, undefined);
    if (next <= lastPage && !this.pages.has(next)) this.#fetchSpecificPage(next, undefined);
  }

  /**
   * Setup pagination bar state (current page, last page, prev/next disabled, page numbers).
   * Call after DOM is ready; bar may not exist yet on first load.
   * Cache initial page twice so Previous is always instant (second run catches late-rendered section).
   */
  #setupPaginationBar() {
    this.#currentPage = this.#getCurrentPageFromURL();
    this.#highestLoadedPage = this.#currentPage;
    const lastPage = this.#getLastPage();
    this.#cacheCurrentPageHTML();

    if (this.dataset.infiniteScroll === 'true') {
      this.#hidePaginationForDenseView();
      this.#setupInfiniteScroll();
      return;
    }

    this.#updatePaginationBarState(lastPage);
    this.#prefetchAdjacentPages();
    requestIdleCallback(() => this.#cacheCurrentPageHTML(), { timeout: 150 });
  }

  /**
   * Infinite scroll — observe sentinel and append next page of cards.
   */
  #setupInfiniteScroll() {
    this.#infiniteObserver?.disconnect();
    this.#infiniteObserver = null;
    this.#clearInfiniteRetryTimer();
    this.#bindInfiniteRetry();

    const lastPage = this.#getLastPage();

    // Single page — no loader or end message
    if (lastPage <= 1) {
      this.#setInfiniteLoaderVisible(false);
      this.#hideInfiniteEnd();
      this.#hideInfiniteError();
      const sentinel = this.#getRef('viewMoreNext');
      if (sentinel instanceof HTMLElement) sentinel.hidden = true;
      return;
    }

    // All pages already on screen (e.g. landed on last page)
    if (this.#highestLoadedPage >= lastPage) {
      this.#hideInfiniteError();
      this.#showInfiniteEnd();
      return;
    }

    // Error state — wait for Try again; do not auto-observe
    if (this.#infinitePausedForError) {
      this.#setInfiniteLoaderVisible(false);
      return;
    }

    // More pages available — hide end message, keep loader hidden until fetch starts
    this.#setInfiniteLoaderVisible(false);
    this.#hideInfiniteEnd();
    this.#hideInfiniteError();

    const sentinel = this.#getRef('viewMoreNext');
    if (!(sentinel instanceof HTMLElement)) return;

    sentinel.hidden = false;

    this.#infiniteObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#loadNextPageInfinite();
        }
      },
      { rootMargin: '600px 0px', threshold: 0 }
    );

    this.#infiniteObserver.observe(sentinel);
  }

  #bindInfiniteRetry() {
    const retry = this.#getRef('infiniteScrollRetry');
    if (!(retry instanceof HTMLElement)) return;
    if (retry.dataset.infiniteRetryBound === 'true') return;
    retry.dataset.infiniteRetryBound = 'true';
    retry.addEventListener('click', this.#onInfiniteRetryClick);
  }

  #onInfiniteRetryClick = () => {
    this.#clearInfiniteRetryTimer();
    this.#infiniteFailCount = 0;
    this.#infinitePausedForError = false;
    this.#hideInfiniteError();
    this.#loadNextPageInfinite().then(() => {
      if (!this.#infinitePausedForError && this.#highestLoadedPage < this.#getLastPage()) {
        this.#setupInfiniteScroll();
      }
    });
  };

  #clearInfiniteRetryTimer() {
    if (this.#infiniteRetryTimer != null) {
      clearTimeout(this.#infiniteRetryTimer);
      this.#infiniteRetryTimer = null;
    }
  }

  #hideInfiniteError() {
    const error = this.#getRef('infiniteScrollError');
    if (error instanceof HTMLElement) {
      error.hidden = true;
    }
  }

  #showInfiniteError() {
    this.#setInfiniteLoaderVisible(false);
    this.#hideInfiniteEnd();

    const error = this.#getRef('infiniteScrollError');
    if (error instanceof HTMLElement) {
      error.hidden = false;
    }

    const sentinel = this.#getRef('viewMoreNext');
    if (sentinel instanceof HTMLElement) sentinel.hidden = true;

    this.#infiniteObserver?.disconnect();
    this.#infiniteObserver = null;
    this.#infinitePausedForError = true;
  }

  /**
   * After a failed fetch: auto-retry a few times, then require Try again.
   */
  #handleInfiniteFetchFail() {
    this.#infiniteFailCount += 1;

    if (this.#infiniteFailCount < PaginatedList.#INFINITE_MAX_AUTO_RETRIES) {
      this.#setInfiniteLoaderVisible(true);
      this.#clearInfiniteRetryTimer();
      this.#infiniteRetryTimer = setTimeout(() => {
        this.#infiniteRetryTimer = null;
        this.#infiniteLoading = false;
        this.#loadNextPageInfinite();
      }, 900);
      return;
    }

    this.#showInfiniteError();
  }

  /**
   * Append the next page of products for infinite scroll.
   */
  async #loadNextPageInfinite() {
    if (this.#infiniteLoading) return;
    if (this.#infinitePausedForError) return;

    const lastPage = this.#getLastPage();
    const nextPage = this.#highestLoadedPage + 1;

    if (nextPage > lastPage) {
      this.#showInfiniteEnd();
      return;
    }

    this.#infiniteLoading = true;
    this.#hideInfiniteEnd();
    this.#hideInfiniteError();
    this.#setInfiniteLoaderVisible(true);

    try {
      if (!this.pages.has(nextPage)) {
        const ok = await this.#fetchSpecificPage(nextPage, undefined);
        if (!ok) {
          this.#handleInfiniteFetchFail();
          return;
        }
      }

      const items = this.#getGridForPage(nextPage);
      const grid = this.#getRef('grid');
      if (!items || items.length === 0 || !(grid instanceof HTMLElement)) {
        this.#handleInfiniteFetchFail();
        return;
      }

      const fragment = document.createDocumentFragment();
      for (const node of items) fragment.appendChild(node);
      grid.appendChild(fragment);

      this.#highestLoadedPage = nextPage;
      this.#currentPage = nextPage;
      this.#infiniteFailCount = 0;
      this.#infinitePausedForError = false;
      this.#processNewElements();
      this.#reflowInjectedOverflowLists(grid, false);

      if (nextPage >= lastPage) {
        this.#showInfiniteEnd();
      }
    } finally {
      // Keep loader visible while a delayed auto-retry is scheduled
      if (this.#infiniteRetryTimer == null) {
        this.#infiniteLoading = false;
        if (this.#highestLoadedPage < lastPage && !this.#infinitePausedForError) {
          this.#setInfiniteLoaderVisible(false);
        }
      }
    }
  }

  /**
   * @param {boolean} visible
   */
  #setInfiniteLoaderVisible(visible) {
    const loader = this.querySelector('[ref="infiniteScrollLoader"]');
    if (loader instanceof HTMLElement) {
      loader.hidden = !visible;
      loader.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }
    if (visible) this.#hideInfiniteEnd();
  }

  #hideInfiniteEnd() {
    const end = this.querySelector('[ref="infiniteScrollEnd"]');
    if (end instanceof HTMLElement) {
      end.hidden = true;
      end.setAttribute('aria-hidden', 'true');
    }
  }

  #showInfiniteEnd() {
    this.#setInfiniteLoaderVisible(false);
    this.#hideInfiniteError();
    this.#infinitePausedForError = false;
    this.#infiniteFailCount = 0;
    this.#clearInfiniteRetryTimer();

    const end = this.querySelector('[ref="infiniteScrollEnd"]');
    if (end instanceof HTMLElement) {
      end.hidden = false;
      end.setAttribute('aria-hidden', 'false');
    }

    const sentinel = this.#getRef('viewMoreNext');
    if (sentinel instanceof HTMLElement) sentinel.hidden = true;

    this.#infiniteObserver?.disconnect();
    this.#infiniteObserver = null;

    document.dispatchEvent(
      new CustomEvent('collection:grid-banners:place', {
        detail: { root: this, forceEnd: true },
      })
    );
  }

  /** Max number of page number links to show (e.g. 1 ... 4 5 6 ... 10) */
  static #MAX_VISIBLE_PAGES = 7;

  /**
   * Build list of page numbers (and ellipsis) to show for limited pagination
   * @param {number} current
   * @param {number} lastPage
   * @returns {Array<number|'ellipsis'>}
   */
  static #getVisiblePageNumbers(current, lastPage) {
    const max = PaginatedList.#MAX_VISIBLE_PAGES;
    if (lastPage <= max) {
      return Array.from({ length: lastPage }, (_, i) => i + 1);
    }
    const out = [];
    const half = Math.floor(max / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(lastPage, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    if (start > 1) {
      out.push(1);
      if (start > 2) out.push('ellipsis');
    }
    for (let i = start; i <= end; i++) out.push(i);
    if (end < lastPage) {
      if (end < lastPage - 1) out.push('ellipsis');
      out.push(lastPage);
    }
    return out;
  }

  /**
   * Update prev/next disabled state, page indicator, and page number links
   */
  /**
   * Build URL for a given page (keeps current path and query params, sets page)
   * @param {number} page
   * @returns {string}
   */
  #getPageURL(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return url.toString();
  }

  #updatePaginationBarState(lastPage) {
    const prevPage = this.#getRef('prevPage');
    const nextPage = this.#getRef('nextPage');
    const paginationBar = this.#getRef('paginationBar');
    const pageNumbersEl = this.#getRef('pageNumbers');

    if (prevPage) prevPage.disabled = this.#currentPage <= 1;
    if (nextPage) nextPage.disabled = this.#currentPage >= lastPage;
    if (paginationBar) paginationBar.setAttribute('data-current-page', String(this.#currentPage));

    if (pageNumbersEl && lastPage > 1) {
      const parts = PaginatedList.#getVisiblePageNumbers(this.#currentPage, lastPage);
      pageNumbersEl.innerHTML = parts
        .map((p) => {
          if (p === 'ellipsis') {
            return '<span class="pagination-bar__ellipsis" aria-hidden="true">…</span>';
          }
          const isCurrent = p === this.#currentPage;
          const href = this.#getPageURL(p);
          return isCurrent
            ? `<span class="pagination-bar__page--current" aria-current="page">${p}</span>`
            : `<a href="${href}" data-page="${p}" class="pagination-bar__page">${p}</a>`;
        })
        .join('');
    }
  }

  /**
   * Go to a specific page. Same flow for Next and Previous: cache current page, fetch target if missing, then update state, DOM, and post-update tasks.
   */
  async #goToPage(pageNum) {
    const grid = this.#getRef('grid');
    if (!grid) return;

    const lastPage = this.#getLastPage();
    if (pageNum < 1 || pageNum > lastPage) return;
    if (pageNum === this.#currentPage) return;

    // Always cache the page we're leaving *before* changing DOM so Previous is as instant as Next
    this.#cacheCurrentPageHTML();

    const usedCachedPage = this.pages.has(pageNum);
    if (!usedCachedPage) {
      const ok = await this.#fetchSpecificPage(pageNum, undefined);
      if (!ok) return;
    }

    const items = this.#getGridForPage(pageNum);
    if (!items || items.length === 0) return;

    this.#currentPage = pageNum;
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(pageNum));
    url.hash = '';
    history.pushState('', '', url.toString());
    this.#updatePaginationBarState(lastPage);

    grid.innerHTML = '';
    const fragment = document.createDocumentFragment();
    for (const node of items) fragment.appendChild(node);
    grid.appendChild(fragment);

    this.#cacheCurrentPageHTML();
    this.#prefetchAdjacentPages();
    this.#processNewElements();
    this.#reflowInjectedOverflowLists(grid, usedCachedPage);

    this.scrollIntoView({
      behavior: mediaQueryLarge.matches ? 'smooth' : 'instant',
      block: 'start',
      inline: 'nearest',
    });
  }

  /**
   * After injecting new cards, fix swatch layout so overflow-list keeps swatches horizontal.
   * Next (fetched): only rAF + idle – do not add delayed reflow.
   * Previous (cached): add one delayed reflow at 80ms so layout is ready (sync path).
   */
  #reflowInjectedOverflowLists(grid, usedCachedPage = false) {
    const overflowLists = Array.from(grid.querySelectorAll('overflow-list'));
    if (overflowLists.length === 0) return;
    overflowLists.forEach((el) => el.removeAttribute('defer'));

    const triggerReflow = () => {
      overflowLists.forEach((el) => {
        if (el.isConnected) {
          el.dispatchEvent(new CustomEvent('reflow', { bubbles: false, detail: { lastVisibleElement: null } }));
        }
      });
    };

    requestAnimationFrame(triggerReflow);
    requestIdleCallback(() => requestAnimationFrame(triggerReflow), { timeout: 100 });
    if (usedCachedPage) {
      setTimeout(() => requestAnimationFrame(triggerReflow), 80);
    }
  }

  /**
   * @param {Object} pageInfo - The page info
   * @param {number} pageInfo.page - The page number
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page < 1) return false;

    return true;
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   * @returns {Promise<boolean>} Whether the fetch succeeded and was cached
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return false;
    try {
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
      this.pages.set(pageNumber, pageContent);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Process newly added elements and apply correct aspect ratios (deferred so paint is not blocked)
   */
  #processNewElements() {
    const grid = this.#getRef('grid');
    requestIdleCallback(() => {
      this.#optimizeInjectedImages(grid);
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
    document.dispatchEvent(
      new CustomEvent('collection:grid-banners:place', {
        detail: {
          root: this,
          forceEnd: this.#highestLoadedPage >= this.#getLastPage(),
          resetPlaced: false,
        },
      })
    );
  }

  /**
   * Demote eager loading on off-screen cards after AJAX page injection or dense load.
   * @param {HTMLElement | null | undefined} grid
   */
  #optimizeInjectedImages(grid) {
    if (!(grid instanceof HTMLElement)) return;

    const isDense = grid.getAttribute('product-grid-view') === 'zoom-out';
    if (isDense) {
      this.#optimizeDenseImages(grid);
      return;
    }

    grid.querySelectorAll('.product-grid__item').forEach((item, index) => {
      const cardGallery = item.querySelector('.card-gallery, .pz-card__media');
      if (!cardGallery) return;

      if (index < 4) {
        const firstImage = cardGallery.querySelector('img');
        if (firstImage instanceof HTMLImageElement && !firstImage.fetchPriority) {
          firstImage.fetchPriority = 'high';
        }
      } else {
        cardGallery.querySelectorAll('img[loading="eager"]').forEach((img) => {
          if (img instanceof HTMLImageElement) img.loading = 'lazy';
        });
      }
    });
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   * For the 'adapt' setting, each product should use its own image's aspect ratio
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   * Only used for non-adaptive modes (square, portrait, landscape)
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    // Batch DOM operations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    // Must match the server-rendered ratios in card-gallery.liquid exactly, otherwise
    // freshly injected (load-more) cards reflow to a different height than the initial
    // cards and appear to "shrink" after loading. Portrait = 2/3, landscape = 16/9.
    portrait: '2 / 3',
    landscape: '16 / 9',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const page = isPrevious ? Number(targetCard.dataset.page) - 1 : Number(targetCard.dataset.page) + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const rawId = this.sectionId && String(this.sectionId).replace(/^shopify-section-/, '');
    let sectionEl = rawId ? parsedPage.getElementById(`shopify-section-${rawId}`) : null;
    if (!sectionEl && parsedPage.body?.firstElementChild?.id?.startsWith?.('shopify-section-')) {
      sectionEl = parsedPage.body.firstElementChild;
    }
    const scope = sectionEl || parsedPage.body;
    const gridElement =
      scope.querySelector('.main-collection-grid .product-grid') ||
      scope.querySelector('[ref="grid"]') ||
      scope.querySelector('.product-grid');
    if (!gridElement) return;
    const cards = gridElement.querySelectorAll(':scope > .product-grid__item, :scope > [ref="cards[]"]');
    return cards.length > 0 ? cards : undefined;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages (Option A: no prefetch)
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#currentPage = 1;
    this.#highestLoadedPage = 1;
    this.#infiniteLoading = false;
    this.#infiniteFailCount = 0;
    this.#infinitePausedForError = false;
    this.#clearInfiniteRetryTimer();
    this.#infiniteObserver?.disconnect();
    this.#infiniteObserver = null;
    this.#setInfiniteLoaderVisible(false);
    this.#hideInfiniteEnd();
    this.#hideInfiniteError();
    const grid = this.#getRef('grid');
    if (grid) {
      delete grid.dataset.denseAllLoaded;
      if (!mediaQueryLarge.matches && grid.getAttribute('product-grid-view') === 'zoom-out') {
        grid.setAttribute('product-grid-view', sessionStorage.getItem('product-grid-view-mobile') || 'default');
      }
    }
    this.#showPaginationBar();
    const lastPage = this.#getLastPage();
    this.#updatePaginationBarState(lastPage);
  };

  /**
   * Re-initialize infinite scroll after section morph (filters, etc.)
   * @param {CustomEvent} event
   */
  #handleSectionLoad = (event) => {
    if (event.detail?.sectionId !== this.sectionId) return;

    requestAnimationFrame(() => {
      this.pages.clear();
      this.#currentPage = this.#getCurrentPageFromURL();
      this.#highestLoadedPage = this.#currentPage;
      this.#infiniteLoading = false;
      this.#infiniteFailCount = 0;
      this.#infinitePausedForError = false;
      this.#clearInfiniteRetryTimer();

      if (this.dataset.infiniteScroll === 'true') {
        this.#setupInfiniteScroll();
        return;
      }

      const lastPage = this.#getLastPage();
      this.#updatePaginationBarState(lastPage);
    });
  };

  /**
   * After facets morph: restore infinite scroll / pagination (FilterUpdate cleared them).
   * Card remount is dispatched from facets with a scoped root — do not mass-refresh here.
   * @param {CustomEvent} event
   */
  #handleFiltersRendered = (event) => {
    if (event.detail?.sectionId && event.detail.sectionId !== this.sectionId) return;
    if (!this.isConnected) return;

    this.#handleSectionLoad(
      /** @type {CustomEvent} */ ({ detail: { sectionId: this.sectionId } })
    );

    // Place banners against the post-filter grid (same as pagination path)
    document.dispatchEvent(
      new CustomEvent('collection:grid-banners:place', {
        detail: { root: this, forceEnd: true, resetPlaced: true },
      })
    );
  };

  /**
   * Load paginated pages into the grid for dense (zoom-out) view.
   * Fetches in small concurrent batches and appends in chunks so the UI
   * stays responsive instead of freezing while hundreds of cards mount.
   * @returns {Promise<boolean>}
   */
  async loadAllPagesForDenseView() {
    if (!mediaQueryLarge.matches || !this.isConnected) return false;

    const grid = this.#getRef('grid');
    if (!grid) return false;
    if (grid.dataset.denseAllLoaded === 'true') {
      this.#optimizeDenseImages(grid);
      return true;
    }

    const lastPage = this.#getLastPage();
    if (lastPage <= 1) {
      grid.dataset.denseAllLoaded = 'true';
      this.#highestLoadedPage = lastPage;
      this.#hidePaginationForDenseView();
      this.#optimizeDenseImages(grid);
      return true;
    }

    this.#cacheCurrentPageHTML();
    this.#hidePaginationForDenseView();

    const FETCH_CONCURRENCY = 3;
    const APPEND_BATCH = 36;

    for (let pageNum = 1; pageNum <= lastPage; pageNum += FETCH_CONCURRENCY) {
      if (!this.isConnected) return false;

      const chunk = [];
      for (let p = pageNum; p < pageNum + FETCH_CONCURRENCY && p <= lastPage; p++) {
        if (!this.pages.has(p)) {
          chunk.push(this.#fetchSpecificPage(p, undefined));
        }
      }
      if (chunk.length > 0) {
        await Promise.all(chunk);
      }
    }

    if (!this.isConnected) return false;

    /** @type {Element[]} */
    const allItems = [];

    for (let pageNum = 1; pageNum <= lastPage; pageNum++) {
      const items = this.#getGridForPage(pageNum);
      if (!items) continue;

      for (const node of items) {
        allItems.push(node);
      }
    }

    if (allItems.length === 0) return false;

    grid.innerHTML = '';

    for (let i = 0; i < allItems.length; i += APPEND_BATCH) {
      if (!this.isConnected) return false;

      const fragment = document.createDocumentFragment();
      const slice = allItems.slice(i, i + APPEND_BATCH);
      for (const node of slice) {
        fragment.appendChild(node);
      }
      grid.appendChild(fragment);
      this.#optimizeDenseImages(grid);

      // Yield so scroll / input stay responsive while remaining cards mount.
      if (i + APPEND_BATCH < allItems.length) {
        await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      }
    }

    grid.dataset.denseAllLoaded = 'true';
    this.#currentPage = 1;
    this.#highestLoadedPage = lastPage;

    const url = new URL(window.location.href);
    url.searchParams.set('page', '1');
    history.replaceState('', '', url.toString());

    this.#hidePaginationForDenseView();
    this.#updatePaginationBarState(lastPage);
    this.#processNewElements();
    // Swatches/details are hidden in dense — skip overflow-list reflow thrash.
    this.#optimizeDenseImages(grid);
    // Cards already initialize via connectedCallback during append — skip mass refresh.
    // Still pass root so the event contract never implies a document-wide remount.
    document.dispatchEvent(
      new CustomEvent('pz-collection-cards:mount', { detail: { dense: true, root: this } })
    );

    return true;
  }

  /**
   * Dense cells are ~11vw — shrink `sizes` and demote offscreen images.
   * @param {HTMLElement | null | undefined} grid
   */
  #optimizeDenseImages(grid) {
    if (!(grid instanceof HTMLElement)) return;

    const denseSizes = '(min-width: 750px) 11vw, 50vw';
    let visibleBudget = 18; // ~2 rows of 9

    grid.querySelectorAll('.product-grid__item').forEach((item) => {
      const media = item.querySelector('.card-gallery, .pz-card__media');
      if (!media) return;

      media.querySelectorAll('img').forEach((img) => {
        if (!(img instanceof HTMLImageElement)) return;

        img.sizes = denseSizes;

        const slide = img.closest('.pz-card__slide, [data-card-slide]');
        const isActive = !slide || slide.classList.contains('is-active') || !slide.hasAttribute('hidden');

        if (!isActive || visibleBudget <= 0) {
          img.loading = 'lazy';
          img.fetchPriority = 'low';
          return;
        }

        visibleBudget -= 1;
        if (img.loading === 'eager') {
          // Keep a small eager budget for first paint only
        }
      });
    });
  }

  /**
   * Restore the grid to a single paginated page after leaving dense view.
   * Re-renders the section so web components (swatches, overflow-list) stay intact.
   */
  async restoreGridFromDenseView() {
    if (!mediaQueryLarge.matches) {
      const grid = this.#getRef('grid');
      if (grid) {
        delete grid.dataset.denseAllLoaded;
        const mobileLayout = sessionStorage.getItem('product-grid-view-mobile') || 'default';
        grid.setAttribute('product-grid-view', mobileLayout);
      }
      this.pages.clear();
      return;
    }

    const grid = this.#getRef('grid');
    if (!grid || grid.dataset.denseAllLoaded !== 'true') return;

    const pageNum = this.#currentPage || 1;
    delete grid.dataset.denseAllLoaded;
    this.pages.clear();

    // Morph copies old `product-grid-view` onto the remorphed grid. Clear zoom-out
    // first so cards reconnect in regular mode (arrows / swatches can bind).
    const storedDesktop = sessionStorage.getItem('product-grid-view-desktop') || 'default';
    grid.setAttribute(
      'product-grid-view',
      storedDesktop === 'zoom-out' ? 'default' : storedDesktop
    );

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(pageNum));
    history.replaceState('', '', url.toString());

    await sectionRenderer.renderSection(this.sectionId, { cache: false });

    // Inject banners immediately with the remorphed section (don't wait on idle/rAF)
    document.dispatchEvent(
      new CustomEvent('collection:grid-banners:place', {
        detail: { root: this, forceEnd: true, resetPlaced: true },
      })
    );

    // Same as filter morph: refresh cards Morph may have reused in place
    document.dispatchEvent(
      new CustomEvent('pz-collection-cards:mount', {
        detail: { root: this, reason: 'dense-restore' },
      })
    );

    this.#currentPage = pageNum;
    this.#highestLoadedPage = pageNum;
    this.#cacheCurrentPageHTML();
    this.#setupPaginationBar();
  }

  #hidePaginationForDenseView() {
    const paginationBar = this.#getRef('paginationBar');
    if (!paginationBar) return;
    paginationBar.hidden = true;
    paginationBar.setAttribute('aria-hidden', 'true');
  }

  #showPaginationBar() {
    const paginationBar = this.#getRef('paginationBar');
    if (!paginationBar) return;
    if (this.#getLastPage() <= 1) return;
    paginationBar.hidden = false;
    paginationBar.removeAttribute('aria-hidden');
  }
}
